import { API_URL } from "env";

export interface GiftListParams {
	sort?: string[];
	page?: number | string;
	limit?: number | string;
	name?: string;
	type?: 1 | 2 | number;
}

export interface GiftResponse<T = any> {
	status: boolean;
	data: T;
	msg?: string;
}

function buildQuery(params?: GiftListParams): string {
	const query = new URLSearchParams();

	if (params) {
		if (params.sort) params.sort.forEach((sortValue) => query.append("sort[]", sortValue));
		if (params.page !== undefined) query.append("page", String(params.page));
		if (params.limit !== undefined) query.append("limit", String(params.limit));
		if (params.name) query.append("name", params.name);
		if (params.type !== undefined) query.append("type", String(params.type));
	}

	return query.toString();
}

function getAuthHeader() {
	const token = localStorage.getItem("token") || "";
	return {
		Authorization: `Bearer ${token}`,
	};
}

async function fetchGifts(params?: GiftListParams): Promise<GiftResponse> {
	const query = buildQuery(params);
	const url = `${API_URL.replace(/\/$/, "")}/api/v1/fe/gifts${query ? `?${query}` : ""}`;

	try {
		const response = await fetch(url, {
			method: "GET",
			headers: {
				Accept: "application/json",
				...getAuthHeader(),
			},
		});

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		return await response.json();
	} catch (error) {
		console.error("Failed to fetch gifts:", error);
		return {
			status: false,
			data: { total: 0, current_page: 1, items: [] },
			msg: "Failed to fetch gifts",
		};
	}
}

async function fetchGiftById(id: number | string): Promise<GiftResponse> {
	try {
		const response = await fetch(`${API_URL.replace(/\/$/, "")}/api/v1/fe/gift/${id}`, {
			method: "GET",
			headers: {
				Accept: "application/json",
				...getAuthHeader(),
			},
		});

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		return await response.json();
	} catch (error) {
		console.error("Failed to fetch gift detail:", error);
		return {
			status: false,
			data: null,
			msg: "Failed to fetch gift detail",
		};
	}
}

async function fetchCustomerRedemptions(params?: GiftListParams): Promise<GiftResponse> {
	const query = buildQuery(params);
	const url = `${API_URL.replace(/\/$/, "")}/api/v1/fe/customer/redemptions${query ? `?${query}` : ""}`;

	try {
		const response = await fetch(url, {
			method: "GET",
			headers: {
				Accept: "application/json",
				...getAuthHeader(),
			},
		});

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		return await response.json();
	} catch (error) {
		console.error("Failed to fetch customer redemptions:", error);
		return {
			status: false,
			data: { total: 0, current_page: 1, items: [] },
			msg: "Failed to fetch customer redemptions",
		};
	}
}

async function redeemGift(id: number | string): Promise<GiftResponse> {
	try {
		const response = await fetch(`${API_URL.replace(/\/$/, "")}/api/v1/fe/gift/${id}/redeem`, {
			method: "POST",
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
				...getAuthHeader(),
			},
			body: JSON.stringify({}),
		});

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		return await response.json();
	} catch (error) {
		console.error("Failed to redeem gift:", error);
		return {
			status: false,
			data: null,
			msg: "Failed to redeem gift",
		};
	}
}

export {
	fetchGifts,
	fetchGiftById,
	fetchCustomerRedemptions,
	redeemGift,
};
